export type CaptionStyleFaq = { question: string; answer: string };

export type CaptionStyleGrouping = "word" | "line";

export type CaptionStyleSpec = {
  font: string;
  case: string;
  highlight: string;
  animation: string;
  background: string;
  bestFor: string;
};

export type CaptionStyle = {
  slug: string;
  name: string;
  templateId: number;
  seoTitle: string;
  seoDescription: string;
  descriptor: string;
  answer: string;
  body: string[];
  faqs: CaptionStyleFaq[];
  related: string[];
  preview: string;
  thumbnail: string;
  grouping: CaptionStyleGrouping;
  plate: "light" | "dark";
  spec: CaptionStyleSpec;
};

export const CAPTION_STYLES_PATH = "/caption-styles";

export const captionStylePath = (slug: string) => `${CAPTION_STYLES_PATH}/${slug}`;

export const CAPTION_STYLE_CTA = "https://crayo.ai/create/auto-clip";

const THUMBNAIL_BASE = "https://cdn-crayo.com/assets/caption-previews";

const GROUPING_LABELS: Record<CaptionStyleGrouping, string> = {
  word: "one word at a time",
  line: "a full line with the spoken word marked",
};

type StyleInput = {
  slug: string;
  name: string;
  templateId: number;
  grouping: CaptionStyleGrouping;
  plate?: "light";
  quickPanel?: false;
  descriptor: string;
  seoDescription: string;
  answer: string;
  body: string[];
  language: string;
  related: string[];
  spec: CaptionStyleSpec;
};

const RELATED_A = ["/features/ai-captions", "/features/auto-clip", "/features/video-editor", "/captions"];
const RELATED_B = ["/features/auto-clip", "/features/ai-captions", "/captions", "/use-cases/podcast-clipping"];
const RELATED_C = ["/features/video-editor", "/features/ai-captions", "/features/auto-clip", "/features/video-cutter"];
const RELATED_D = ["/features/ai-captions", "/captions", "/features/video-editor", "/features/video-crop"];

const buildFaqs = (input: StyleInput): CaptionStyleFaq[] => {
  const groupingNote =
    input.grouping === "word"
      ? "with the one word at a time grouping switched on"
      : "with line grouping, which is the default";
  const where =
    input.quickPanel === false
      ? `${input.name} is listed in the advanced video editor's caption template picker rather than the quick Styles panel. Pick it there and it applies to every caption on the timeline.`
      : `Auto Clip clips open in the Crayo editor, and the Styles panel lists ${input.name} ${groupingNote}. Tap it once and it applies to every line of the transcript.`;

  return [
    {
      question: "Can I change the colours?",
      answer: `Yes. Pick ${input.name}, then open Customise in the editor to change the text colour, the highlight colour, the font and where the captions sit. The change applies to the whole transcript at once, so you keep the same motion and layout with your own palette.`,
    },
    {
      question: `Does it work with ${input.language} captions?`,
      answer: `Yes. The style is language neutral: the font, colours and animation apply to whatever language Crayo transcribes. ${input.language} uses Latin letters, which ${input.spec.font} is built for. For scripts such as Arabic, Hebrew or Japanese, swap the font in Customise and keep the rest of the look.`,
    },
    {
      question: "Can I use it in the editor and Auto Clip?",
      answer: where,
    },
    {
      question: "Is it free?",
      answer: `There is no separate charge for caption styles. Every Crayo plan, Hobby, Clipper and Pro, includes ${input.name} and every other style in this gallery, plus the editor to customise them. Crayo itself is a paid subscription.`,
    },
  ];
};

const define = (input: StyleInput): CaptionStyle => {
  const thumbnail = `${THUMBNAIL_BASE}/${input.templateId}.png`;

  return {
    slug: input.slug,
    name: input.name,
    templateId: input.templateId,
    seoTitle: `${input.name} Caption Style for TikTok and Shorts`,
    seoDescription: input.seoDescription,
    descriptor: input.descriptor,
    answer: input.answer,
    body: input.body,
    faqs: buildFaqs(input),
    related: input.related,
    preview: thumbnail,
    thumbnail,
    grouping: input.grouping,
    plate: input.plate ?? "dark",
    spec: input.spec,
  };
};

export const CAPTION_STYLES: CaptionStyle[] = [
  define({
    slug: "classic",
    name: "Classic",
    templateId: 1,
    grouping: "line",
    descriptor: "White SF Pro Display with a soft shadow, fading in one line at a time.",
    seoDescription:
      "Classic is the default Crayo caption: white SF Pro Display, a soft black shadow and a gentle fade, shown one line at a time. Clean enough for any clip.",
    answer:
      "Classic is the default Crayo caption: white SF Pro Display Bold in sentence case, carried by a layered black shadow instead of a stroke, with each line fading in. It suits talking-head podcast clips and interviews where the captions should read cleanly without pulling attention from the speaker.",
    body: [
      "Classic is the style every Crayo project starts with. The lettering is SF Pro Display Bold in white with tight letter spacing, and five stacked black blur shadows give it enough contrast to sit on bright footage or a face without a hard outline. Nothing is uppercase, so long sentences still look like sentences.",
      "The only motion is a fade over the first 40 percent of each line's time on screen, and the spoken word is not recoloured. That restraint is the point: use it on podcast cuts, founder interviews and educational clips where the words matter more than the effect.",
      "If you want the same look with more punch, the Glow Line and Clean Word styles share the font and shadow but add a glow or switch to one word at a time.",
    ],
    language: "Spanish",
    related: RELATED_A,
    spec: {
      font: "SF Pro Display Bold",
      case: "Sentence case",
      highlight: "None, every word stays white",
      animation: "Line fades in",
      background: "Layered black blur shadow, no stroke or box",
      bestFor: "Podcast clips, interviews and educational talking heads",
    },
  }),
  define({
    slug: "clean-word",
    name: "Clean Word",
    templateId: 2,
    grouping: "word",
    descriptor: "The Classic look shown one word at a time, with no extra motion.",
    seoDescription:
      "Clean Word shows one white SF Pro Display word at a time with a soft black shadow and no animation. A minimal word-by-word caption for fast talkers.",
    answer:
      "Clean Word shows a single white SF Pro Display Bold word at a time, lifted off the footage by the same layered black shadow as Classic, with no entrance animation. It suits fast-paced talking-head clips and stream highlights where the viewer should read exactly one word per beat.",
    body: [
      "Clean Word keeps the Classic typography, white SF Pro Display Bold in sentence case with tight spacing, and changes how it is grouped: each spoken word appears on its own instead of as part of a line. Five black blur shadows handle legibility, so there is no stroke and no box.",
      "There is no entrance or exit animation. Words simply cut in and out on the transcript timing, which keeps the rhythm of fast speech intact and avoids the bouncing that can tire viewers over a long clip.",
      "Reach for it on rapid stream commentary, rants and quick explainer clips. For a line-based version of the same look use Classic; for a glowing version use Glow Line.",
    ],
    language: "Portuguese",
    related: RELATED_C,
    spec: {
      font: "SF Pro Display Bold",
      case: "Sentence case",
      highlight: "None, each word is shown alone in white",
      animation: "None, words cut in on the transcript timing",
      background: "Layered black blur shadow, no stroke or box",
      bestFor: "Fast commentary, stream highlights and quick explainers",
    },
  }),
  define({
    slug: "cyan-pop",
    name: "Cyan Pop",
    templateId: 215,
    grouping: "word",
    descriptor: "Bright cyan Francois One with a black stroke, popping in word by word.",
    seoDescription:
      "Cyan Pop is a word-by-word caption in bright cyan Francois One with a 3px black stroke. Each word squashes in and springs to size for high-energy clips.",
    answer:
      "Cyan Pop sets one word at a time in bright cyan Francois One with a 3px black stroke, and each word squashes in from narrow, overshoots to 110 percent and settles. It suits high-energy stream clips, gaming moments and reaction cuts where every word should land with a hit.",
    body: [
      "Cyan Pop is built for energy. The text colour is a saturated cyan, the typeface is Francois One, a tall condensed sans, and a 3px black stroke keeps it readable on any background without a box.",
      "The motion is the signature: each word fades in almost instantly while scaling from a squashed 40 percent width and 80 percent height up to 110 percent in its first 30 percent on screen, then eases back to full size over the next 30 percent. The effect reads as a pop on every syllable.",
      "Use it on stream highlights, gaming clips and reaction content. It is too lively for a calm interview; pick Classic or Light Box for those.",
    ],
    language: "French",
    related: RELATED_B,
    spec: {
      font: "Francois One",
      case: "As spoken",
      highlight: "Whole word in cyan, shown one word at a time",
      animation: "Word squashes in and pops to size",
      background: "3px black stroke, no box",
      bestFor: "Gaming, stream highlights and reaction clips",
    },
  }),
  define({
    slug: "glow-line",
    name: "Glow Line",
    templateId: 7,
    grouping: "line",
    descriptor: "White lines with a white glow that stretch in and drift gently.",
    seoDescription:
      "Glow Line pairs white SF Pro Display with a soft white glow and a thin black edge. Lines stretch in, drift subtly and fade out, ideal for cinematic clips.",
    answer:
      "Glow Line is white SF Pro Display Bold wrapped in a soft white glow and a thin black edge, with each line stretching in horizontally, drifting by a pixel while it is on screen and fading out at the end. It suits cinematic podcast clips and storytelling cuts that want a little life without loud motion.",
    body: [
      "Glow Line uses the Classic font, SF Pro Display Bold in sentence case, but swaps the heavy shadow for two 1px black edges plus two white glows at 12px and 15px blur. On dark or moody footage the glow makes the captions feel lit rather than pasted on.",
      "Each line scales in from 60 percent width while fading up over its first 10 percent on screen. While it sits on screen it drifts one pixel left and right over 40 frames and one pixel up and down over 15 frames, a slow wobble that keeps the text feeling alive, then fades out in the last tenth of its duration.",
      "It works best on cinematic storytelling, podcast clips over b-roll and motivational cuts. For a glow on a single word, see Vintage Serif; Yellow Wave gives full lines a yellow glow instead.",
    ],
    language: "German",
    related: RELATED_D,
    spec: {
      font: "SF Pro Display Bold",
      case: "Sentence case",
      highlight: "None, every word stays white",
      animation: "Line stretches in, drifts gently, fades out",
      background: "White glow with a 1px black edge, no box",
      bestFor: "Cinematic storytelling and podcast clips over b-roll",
    },
  }),
  define({
    slug: "blue-keyword",
    name: "Blue Keyword",
    templateId: 3,
    grouping: "line",
    descriptor: "Condensed uppercase with a solid shadow and the spoken word in blue.",
    seoDescription:
      "Blue Keyword is uppercase Barlow Condensed with a solid 45 degree shadow. The spoken word and keywords turn bright blue as each word rises into place.",
    answer:
      "Blue Keyword sets full lines in uppercase Barlow Condensed with a solid black 45 degree shadow, turning the spoken word and any keyword bright blue as each word rises into place. It suits educational clips and interview highlights where a few words per sentence carry the meaning.",
    body: [
      "Blue Keyword is a line style with a strong emphasis system. Upcoming words are white, the word being spoken flips to a saturated blue, and keywords use the same blue when keyword highlighting is on. The typeface is Barlow Condensed in uppercase, so a full sentence fits on one line.",
      "Instead of a blurred shadow it uses a solid shadow offset at 45 degrees with a depth of 2.5, which gives the letters a printed, sticker-like edge. Words rise 12px into their baseline as they appear.",
      "It is a good fit for educational breakdowns, finance and business clips, and interviews where the editor wants to steer the eye to the key terms. Lime Slab and Slab Yellow do the same job with a heavier typeface.",
    ],
    language: "Italian",
    related: RELATED_A,
    spec: {
      font: "Barlow Condensed",
      case: "Uppercase",
      highlight: "Spoken word and keywords in bright blue",
      animation: "Words rise into place",
      background: "Solid 45 degree black shadow, no stroke or box",
      bestFor: "Educational, business and interview clips",
    },
  }),
  define({
    slug: "green-keyword",
    name: "Green Keyword",
    templateId: 4,
    grouping: "word",
    descriptor: "Rounded Gabarito with a thin stroke and glow, keywords in green.",
    seoDescription:
      "Green Keyword shows one Gabarito word at a time with a thin black stroke and a soft white glow. Keywords flip to bright green and each word fades away.",
    answer:
      "Green Keyword shows one Gabarito word at a time in white with a 1.5px black stroke and a soft white glow, flipping keywords to bright green and fading each word out as the next arrives. It suits educational and how-to clips where a highlighted term per sentence helps viewers follow along.",
    body: [
      "Green Keyword uses Gabarito, a rounded geometric sans, in sentence case. A thin 1.5px black stroke and a white glow offset 3px downward give the word a soft halo that reads on both bright and dark footage.",
      "Words are shown one at a time and each one fades out over the last 40 percent of its time on screen, so the transition between words feels like a dissolve rather than a cut. When keyword highlighting is on, the important words switch from white to a vivid green.",
      "Pick it for tutorials, product explainers and finance clips where a highlighted word reinforces the point. For the same keyword idea on a full line, see Blue Keyword.",
    ],
    language: "Dutch",
    related: RELATED_C,
    spec: {
      font: "Gabarito",
      case: "Sentence case",
      highlight: "Keywords in bright green, one word shown at a time",
      animation: "Each word fades out",
      background: "1.5px black stroke with a soft white glow",
      bestFor: "Tutorials, explainers and finance clips",
    },
  }),
  define({
    slug: "vintage-serif",
    name: "Vintage Serif",
    templateId: 5,
    grouping: "word",
    descriptor: "An old-style serif with a soft white glow, one word at a time.",
    seoDescription:
      "Vintage Serif shows one word at a time in IM FELL English, an old-style serif, with a soft white glow and a faint shadow. A quiet, literary caption style.",
    answer:
      "Vintage Serif shows one word at a time in IM FELL English, an old-style serif, in white with a triple white glow and a faint dark shadow and no animation. It suits storytelling, history and quote clips where a period feel matters more than energy.",
    body: [
      "Vintage Serif is the one old-style serif in the gallery. IM FELL English is cut from seventeenth century type, with uneven strokes and a slightly worn edge, and here it is set in white, sentence case, one word at a time.",
      "Three white glows at 10px blur and two faint black shadows at 1px and 2px give the letters a candlelit softness without a stroke or a box. There is no entrance animation, so the words simply appear on their timing.",
      "Use it on narrated stories, history and book summaries, and quote clips over slow footage. It is a poor match for gaming or fast commentary, where Cyan Pop or Hard Pop will read better.",
    ],
    language: "Spanish",
    related: RELATED_D,
    spec: {
      font: "IM FELL English",
      case: "Sentence case",
      highlight: "None, each word is shown alone in white",
      animation: "None, words cut in on the transcript timing",
      background: "Triple white glow with a faint dark shadow",
      bestFor: "Storytelling, history and quote clips",
    },
  }),
  define({
    slug: "light-box",
    name: "Light Box",
    templateId: 6,
    grouping: "line",
    plate: "light",
    descriptor: "Poppins in a light grey box, words turning from grey to black as spoken.",
    seoDescription:
      "Light Box puts Poppins Medium in a rounded light grey box. Upcoming words sit in grey and turn black as they are spoken, a calm style for interviews.",
    answer:
      "Light Box sets each line of Poppins Medium inside a rounded light grey box, with upcoming words in grey that turn black as they are spoken. It suits interviews, corporate clips and educational content that should look like subtitles rather than effects.",
    body: [
      "Light Box is the quietest style in the gallery and the only one on a light plate. Poppins Medium in sentence case sits inside a light grey box with 4px rounded corners and 5px of padding, so it reads like a caption card from a broadcast interview.",
      "The karaoke effect is subtle: words that have not been spoken yet are a mid grey, and each word turns black the moment it is said. There is no stroke, no shadow and no entrance animation.",
      "It is the right pick for corporate interviews, founder stories, LinkedIn-style clips and any talking head where the brand wants a clean, professional subtitle. For a dark version of the same idea see Black Box.",
    ],
    language: "Portuguese",
    related: RELATED_B,
    spec: {
      font: "Poppins Medium",
      case: "Sentence case",
      highlight: "Words turn from grey to black as spoken",
      animation: "None",
      background: "Light grey rounded box",
      bestFor: "Corporate interviews, founder stories and professional clips",
    },
  }),
  define({
    slug: "comic-bounce",
    name: "Comic Bounce",
    templateId: 67,
    grouping: "line",
    descriptor: "Komika Axis comic lettering with a thick stroke, bouncing in and out.",
    seoDescription:
      "Comic Bounce sets each line in Komika Axis comic lettering with a 5px black stroke. Lines bounce in, hold and bounce out, a loud style for reaction clips.",
    answer:
      "Comic Bounce sets each line in Komika Axis, a comic typeface that renders in capitals, in white with a 5px black stroke, and every line bounces in and bounces out. It suits reaction content, gaming clips and meme-style edits where the captions are part of the joke.",
    body: [
      "Comic Bounce uses Komika Axis, a comic book display face whose letterforms are all capitals, with wider 0.15em word spacing so the line breathes. A 5px black stroke does the work of a box.",
      "Each line fades up while scaling from 90 percent to 103 percent and settling at 100 percent, a quick bounce that takes a tenth of its duration. At the end the line pops to 102 percent, shrinks to 90 percent and fades, so every caption arrives and leaves with a bump.",
      "It belongs on reaction clips, gaming highlights and comedy. Pair it with sound effects and quick cuts; it would feel out of place on a calm interview.",
    ],
    language: "French",
    related: RELATED_C,
    spec: {
      font: "Komika Axis",
      case: "Capitals, from the typeface",
      highlight: "None, every word stays white",
      animation: "Line bounces in and bounces out",
      background: "5px black stroke, no box",
      bestFor: "Reaction, gaming and comedy clips",
    },
  }),
  define({
    slug: "hard-pop",
    name: "Hard Pop",
    templateId: 9,
    grouping: "word",
    descriptor: "Eras Bold with a thick stroke and hard offset shadow, popping word by word.",
    seoDescription:
      "Hard Pop shows one Eras Bold word at a time with a 4px black stroke and a hard offset shadow. Each word scales up past full size and snaps back.",
    answer:
      "Hard Pop shows one word at a time in Eras Bold ITC, white with a 4px black stroke and a hard black shadow offset to the lower right, and every word scales from 60 percent up to 120 percent before snapping back to size. It suits hype moments, sports clips and stream highlights where each word should hit.",
    body: [
      "Hard Pop is a punchy one-word style. Eras Bold ITC is a wide, heavy sans, and here it is set in white with a 4px black stroke plus two offset black shadows at 4px and 2px, which gives each word a chunky 3D edge.",
      "The animation is a three-stage pop: the word scales from 60 percent to 120 percent in the first 30 percent of its time, drops to 90 percent over the next 30 percent, then eases to full size. It is the most forceful entrance in the gallery.",
      "Use it on hype content, sports and fitness clips, and gaming kills or clutch plays. For a similar hit with a comic face, see Cyan Pop or Comic Zoom.",
    ],
    language: "German",
    related: RELATED_A,
    spec: {
      font: "Eras Bold ITC",
      case: "As spoken",
      highlight: "Whole word in white, shown one word at a time",
      animation: "Word scales past full size and snaps back",
      background: "4px black stroke with a hard offset shadow",
      bestFor: "Hype, sports, fitness and gaming clips",
    },
  }),
  define({
    slug: "ripple-caps",
    name: "Ripple Caps",
    templateId: 18,
    grouping: "word",
    descriptor: "Uppercase Francois One with a thick stroke, rippling in from the centre.",
    seoDescription:
      "Ripple Caps is uppercase Francois One in white with a 5px black stroke. Letters scale in from the centre outward in a ripple, one word at a time.",
    answer:
      "Ripple Caps shows one uppercase Francois One word at a time in white with a 5px black stroke, scaling in from the centre outward in a staggered ripple. It suits high-energy commentary and motivational clips where a tall, bold word should fill the frame.",
    body: [
      "Ripple Caps uses Francois One, a tall condensed sans, in uppercase white with a heavy 5px black stroke. There is no shadow or box; the stroke alone keeps it readable on busy footage.",
      "The entrance is a ripple: each element scales from 67 percent to full size over roughly the first third of its time on screen, staggered outward from the centre, so the word seems to unfold from its middle.",
      "It fits motivational cuts, fitness content and commentary with a strong delivery. Clean Caps uses the same ripple on full lines in Montserrat Black if you want a wider typeface.",
    ],
    language: "Italian",
    related: RELATED_D,
    spec: {
      font: "Francois One",
      case: "Uppercase",
      highlight: "Whole word in white, shown one word at a time",
      animation: "Ripples in from the centre",
      background: "5px black stroke, no box",
      bestFor: "Motivational, fitness and commentary clips",
    },
  }),
  define({
    slug: "yellow-wave",
    name: "Yellow Wave",
    templateId: 8,
    grouping: "line",
    descriptor: "Yellow Chivo with a yellow glow, words waving up one after another.",
    seoDescription:
      "Yellow Wave sets full lines in bright yellow Chivo with a matching yellow glow. Words slide up one after another in a wave, a warm style for storytelling.",
    answer:
      "Yellow Wave sets full lines in bright yellow Chivo with a matching yellow glow, and the words slide up into place one after another in a forward wave. It suits storytelling, lifestyle and vlog clips that want a warm, hand-made feel.",
    body: [
      "Yellow Wave is the softest of the yellow styles. Chivo in sentence case is set in a bright yellow, and instead of a stroke it carries a yellow glow offset 3px with a 14px blur, so the text looks lit from behind.",
      "Lines appear as a wave: each word rises 6px into place, staggered forward word by word, so a sentence rolls onto the screen left to right.",
      "Use it for vlogs, travel and lifestyle edits, and narrated stories over warm footage. For a yellow word-by-word style with more punch, see Yellow Comic or Yellow Wiggle.",
    ],
    language: "Dutch",
    related: RELATED_B,
    spec: {
      font: "Chivo",
      case: "Sentence case",
      highlight: "None, every word stays yellow",
      animation: "Words wave up one after another",
      background: "Yellow glow, no stroke or box",
      bestFor: "Vlogs, lifestyle and narrated story clips",
    },
  }),
  define({
    slug: "green-caps",
    name: "Green Caps",
    templateId: 24,
    grouping: "word",
    descriptor: "Uppercase green Encode Sans with a thick stroke, keywords growing larger.",
    seoDescription:
      "Green Caps shows one uppercase Encode Sans word at a time in bright green with a 5px black stroke and an offset shadow. Keywords grow 15 percent larger.",
    answer:
      "Green Caps shows one uppercase Encode Sans word at a time in bright green with a 5px black stroke and a hard offset shadow, and keywords scale up 15 percent as they land. It suits money, finance and gaming clips where green already means something.",
    body: [
      "Green Caps is a bold one-word style in a saturated green. Encode Sans is set in uppercase with a 5px black stroke and a black shadow offset 4px to the lower right, which gives each word a solid, printed edge.",
      "Emphasis is by size rather than colour: when keyword highlighting is on, keywords scale from 100 to 115 percent over the first 20 percent of their time on screen, so the important word physically grows. Other words appear without motion.",
      "It is a natural fit for finance, side hustle and money content, and for gaming where green reads as a win. Lime Slab offers a line-based green highlight if you prefer full sentences.",
    ],
    language: "Spanish",
    related: RELATED_C,
    spec: {
      font: "Encode Sans",
      case: "Uppercase",
      highlight: "Keywords grow 15 percent larger, text in green",
      animation: "Keyword scale, otherwise none",
      background: "5px black stroke with a hard offset shadow",
      bestFor: "Finance, money and gaming clips",
    },
  }),
  define({
    slug: "comic-red",
    name: "Comic Red",
    templateId: 34,
    grouping: "line",
    descriptor: "Bangers comic lettering with a thick stroke, the spoken word in red.",
    seoDescription:
      "Comic Red sets full lines in Bangers comic lettering with a 5px black stroke, turning the spoken word red as it is said. A loud style for reaction clips.",
    answer:
      "Comic Red sets full lines in Bangers, a comic book typeface, in white with a 5px black stroke and flips the spoken word to red as it is said. It suits reaction, gaming and comedy clips where a comic look fits the tone.",
    body: [
      "Comic Red uses Bangers, a loud comic display face, with wider 0.15em word spacing and a 5px black stroke. Upcoming and spoken words are white; the word being said turns a deep red, so the eye follows the speech across the line.",
      "There is no entrance animation on the line itself. The red highlight moving word to word provides the motion, which keeps the style readable even on short, fast captions.",
      "It is made for reaction videos, gaming commentary and comedy sketches. Pink Comic and Yellow Comic use the same typeface with a different colour and an added entrance.",
    ],
    language: "Portuguese",
    related: RELATED_A,
    spec: {
      font: "Bangers",
      case: "Capitals, from the typeface",
      highlight: "Spoken word in red",
      animation: "None, the highlight moves word to word",
      background: "5px black stroke, no box",
      bestFor: "Reaction, gaming and comedy clips",
    },
  }),
  define({
    slug: "slab-yellow",
    name: "Slab Yellow",
    templateId: 37,
    grouping: "line",
    descriptor: "Uppercase Alfa Slab One with a thick stroke, the spoken word in yellow.",
    seoDescription:
      "Slab Yellow is uppercase Alfa Slab One with a 5px black stroke and hard shadow. The spoken word turns yellow as each line zooms in, a classic hook style.",
    answer:
      "Slab Yellow sets full lines in uppercase Alfa Slab One, white with a 5px black stroke and a hard offset shadow, turning the spoken word yellow as each line zooms in from the centre. It suits business, podcast and motivational clips, and it is the look often called the Hormozi style.",
    body: [
      "Slab Yellow is the heavy slab serif style many clippers ask for by name. Alfa Slab One in uppercase carries a 5px black stroke and a sharp black shadow offset 4px, and the word being spoken flips from white to a bright yellow.",
      "Each line zooms in from 70 percent to full size over the first 15 percent of its duration, a quick snap that keeps the rhythm on cuts. Bold white capitals with a yellow spoken-word highlight is the combination often called the Hormozi style, and this template reproduces it without any extra setup.",
      "Use it on business and sales clips, podcast highlights and motivational cuts. Lime Slab and Aqua Slab give the same typeface a different accent colour.",
    ],
    language: "French",
    related: RELATED_B,
    spec: {
      font: "Alfa Slab One",
      case: "Uppercase",
      highlight: "Spoken word in yellow",
      animation: "Line zooms in from the centre",
      background: "5px black stroke with a hard offset shadow",
      bestFor: "Business, podcast and motivational clips",
    },
  }),
  define({
    slug: "comic-zoom",
    name: "Comic Zoom",
    templateId: 39,
    grouping: "word",
    descriptor: "Bangers comic lettering with a thick stroke, shrinking away word by word.",
    seoDescription:
      "Comic Zoom shows one Bangers word at a time in white with a 5px black stroke and a soft shadow. Each word zooms out as the next one arrives.",
    answer:
      "Comic Zoom shows one word at a time in Bangers comic lettering, white with a 5px black stroke and a soft black shadow, and each word shrinks away at the end of its time on screen. It suits gaming and reaction clips with quick, punchy delivery.",
    body: [
      "Comic Zoom pairs Bangers with a 5px black stroke and a softer shadow than Comic Red, offset 4px with a 10px blur, so the word floats a little off the footage.",
      "Words are shown one at a time and the motion is on the exit: in the last 15 percent of its duration each word zooms out from full size to 70 percent, which makes the next word feel like it is pushing in.",
      "It suits gaming clips, reaction content and energetic commentary. For the same typeface in yellow with an entrance instead, see Yellow Comic.",
    ],
    language: "German",
    related: RELATED_D,
    spec: {
      font: "Bangers",
      case: "Capitals, from the typeface",
      highlight: "Whole word in white, shown one word at a time",
      animation: "Word zooms out at the end",
      background: "5px black stroke with a soft shadow",
      bestFor: "Gaming, reaction and energetic commentary",
    },
  }),
  define({
    slug: "brush-tilt",
    name: "Brush Tilt",
    templateId: 48,
    grouping: "word",
    descriptor: "Uppercase Knewave brush lettering with a drop shadow, tilting into place.",
    seoDescription:
      "Brush Tilt shows one uppercase Knewave word at a time, white with a soft drop shadow and no stroke, rotating slightly into place. A hand-painted look.",
    answer:
      "Brush Tilt shows one uppercase Knewave word at a time, a brush-script display face in white with a soft black drop shadow and no stroke, rotating a few degrees into place. It suits lifestyle, fitness and creative clips that want a hand-painted feel.",
    body: [
      "Brush Tilt is the painterly option. Knewave looks like it was brushed on, and setting it in uppercase white with a black shadow offset 3px across and 5px down at 6px blur keeps the strokes visible without a hard outline.",
      "Each word rotates in from a slight counter-clockwise tilt to level over the first 30 percent of its time on screen, a small flick that matches the hand-made typeface.",
      "It works on fitness and sports clips, creative vlogs, streetwear and music content. For another informal face, Marker uses Permanent Marker with a stroke and a fade.",
    ],
    language: "Italian",
    related: RELATED_C,
    spec: {
      font: "Knewave",
      case: "Uppercase",
      highlight: "Whole word in white, shown one word at a time",
      animation: "Word rotates slightly into place",
      background: "Soft black drop shadow, no stroke or box",
      bestFor: "Fitness, creative vlogs and music clips",
    },
  }),
  define({
    slug: "red-pill",
    name: "Red Pill",
    templateId: 54,
    grouping: "word",
    descriptor: "Uppercase Fjalla One inside a red rounded pill, sliding up word by word.",
    seoDescription:
      "Red Pill shows one uppercase Fjalla One word at a time in white on a red rounded pill that follows the speech. Each word slides up into place.",
    answer:
      "Red Pill shows one uppercase Fjalla One word at a time in white, sitting inside a red rounded pill with 20px corners that marks the spoken word, and each word slides up 20px into place. It suits news-style commentary, hot takes and clips where a solid colour block should frame every word.",
    body: [
      "Red Pill is the only style with a coloured fill behind the spoken word. Fjalla One, a condensed sans, is set in uppercase white, and the active word gets a red pill with a 20px radius and 5px by 7px padding instead of a stroke or shadow.",
      "Each word pans up from 20px below into place over the first 15 percent of its duration, a short slide that keeps the pill feeling attached to the speech.",
      "Use it on commentary, news and opinion clips, and on product or sales content where a red block reads as urgent. Blue Box and Black Box give whole lines a box if you want the same solidity with full sentences.",
    ],
    language: "Dutch",
    related: RELATED_A,
    spec: {
      font: "Fjalla One",
      case: "Uppercase",
      highlight: "Spoken word on a red rounded pill",
      animation: "Word slides up into place",
      background: "Red pill behind the spoken word, no stroke",
      bestFor: "Commentary, news and opinion clips",
    },
  }),
  define({
    slug: "marker",
    name: "Marker",
    templateId: 58,
    grouping: "word",
    descriptor: "Uppercase Permanent Marker with a thick stroke, fading in word by word.",
    seoDescription:
      "Marker shows one uppercase Permanent Marker word at a time in white with a 5px black stroke, fading in. A hand-written caption for casual, comedy clips.",
    answer:
      "Marker shows one uppercase Permanent Marker word at a time in white with a 5px black stroke and a simple fade in. It suits casual vlogs, comedy and behind-the-scenes clips where the captions should feel hand-written.",
    body: [
      "Marker uses Permanent Marker, a felt-tip handwriting face, set in uppercase white with a heavy 5px black stroke. The stroke keeps the thin pen strokes legible on any background.",
      "The animation is a fade over the first 40 percent of each word's time on screen and nothing else, so the handwritten letterforms carry the personality while the motion stays calm.",
      "It is a good match for casual vlogs, comedy, pranks and behind-the-scenes clips. Brush Tilt gives a similar hand-made feel with a painted face and a tilt.",
    ],
    language: "Spanish",
    related: RELATED_D,
    spec: {
      font: "Permanent Marker",
      case: "Uppercase",
      highlight: "Whole word in white, shown one word at a time",
      animation: "Word fades in",
      background: "5px black stroke, no box",
      bestFor: "Casual vlogs, comedy and behind-the-scenes clips",
    },
  }),
  define({
    slug: "aqua-slab",
    name: "Aqua Slab",
    templateId: 61,
    grouping: "line",
    descriptor: "Uppercase Alfa Slab One in aqua with no stroke, fading out line by line.",
    seoDescription:
      "Aqua Slab sets full lines in uppercase Alfa Slab One in bright aqua with no stroke or shadow. Each line fades out. A bold, clean style for dark footage.",
    answer:
      "Aqua Slab sets full lines in uppercase Alfa Slab One in a bright aqua with no stroke, shadow or box, and each line fades out as it ends. It suits clips over dark footage, tech and gaming content where the colour alone provides the contrast.",
    body: [
      "Aqua Slab strips the slab serif back to colour. Alfa Slab One in uppercase is set in a saturated aqua with nothing behind it, so it is the cleanest of the slab styles and looks best on dark or desaturated video.",
      "The only motion is a fade out over the last 40 percent of each line. Every word is the same colour, so there is no spoken-word highlight.",
      "Pick it for tech reviews, gaming over dark scenes and night footage. On bright backgrounds choose Slab Yellow or Lime Slab, which add a stroke.",
    ],
    language: "Portuguese",
    related: RELATED_B,
    spec: {
      font: "Alfa Slab One",
      case: "Uppercase",
      highlight: "None, every word stays aqua",
      animation: "Line fades out",
      background: "None",
      bestFor: "Tech, gaming and dark footage",
    },
  }),
  define({
    slug: "lime-slab",
    name: "Lime Slab",
    templateId: 66,
    grouping: "line",
    descriptor: "Uppercase Alfa Slab One with a thick stroke, the spoken word in lime.",
    seoDescription:
      "Lime Slab is uppercase Alfa Slab One in white with a 5px black stroke, turning the spoken word lime green as words rise into place. Bold, easy to follow.",
    answer:
      "Lime Slab sets full lines in uppercase Alfa Slab One, white with a 5px black stroke, turning the spoken word lime green as each word rises into place. It suits podcast highlights, fitness and money clips that want a bold karaoke highlight.",
    body: [
      "Lime Slab is the green sibling of Slab Yellow. Alfa Slab One in uppercase white with a 5px black stroke stays readable on anything, and the word being spoken switches to a lime green.",
      "Each word rises 12px into its baseline as it appears, so a sentence builds word by word while the highlight moves along it. There is no shadow beyond the stroke.",
      "It is a strong choice for podcast highlights, fitness and money content, and any clip where viewers should follow along word for word. Slab Yellow swaps the accent for yellow and adds a zoom.",
    ],
    language: "French",
    related: RELATED_C,
    spec: {
      font: "Alfa Slab One",
      case: "Uppercase",
      highlight: "Spoken word in lime green",
      animation: "Words rise into place",
      background: "5px black stroke, no box",
      bestFor: "Podcast highlights, fitness and money clips",
    },
  }),
  define({
    slug: "bold-zoom",
    name: "Bold Zoom",
    templateId: 72,
    grouping: "word",
    descriptor: "Uppercase Sigmar One with a thick stroke, zooming in word by word.",
    seoDescription:
      "Bold Zoom shows one uppercase Sigmar One word at a time, white with a 5px black stroke, zooming in from the centre. A chunky, high-impact word style.",
    answer:
      "Bold Zoom shows one uppercase Sigmar One word at a time, white with a 5px black stroke, zooming in from 70 percent to full size. It suits hype clips, sports and short punchy quotes where a single heavy word should own the frame.",
    body: [
      "Bold Zoom uses Sigmar One, one of the heaviest faces in the gallery, in uppercase white with a 5px black stroke and no shadow or box.",
      "Each word zooms in from 70 percent to full size over the first 15 percent of its duration. The entrance is quick and clean, with no overshoot, which keeps very short words from feeling jittery.",
      "Use it on hype and sports clips, quotes and countdowns. Blue Box puts the same typeface in a blue box on full lines.",
    ],
    language: "German",
    related: RELATED_A,
    spec: {
      font: "Sigmar One",
      case: "Uppercase",
      highlight: "Whole word in white, shown one word at a time",
      animation: "Word zooms in from the centre",
      background: "5px black stroke, no box",
      bestFor: "Hype, sports and short punchy quotes",
    },
  }),
  define({
    slug: "yellow-serif",
    name: "Yellow Serif",
    templateId: 78,
    grouping: "word",
    descriptor: "Condensed yellow Imbue serif with no stroke, sliding in from the left.",
    seoDescription:
      "Yellow Serif shows one word at a time in Imbue, a tall condensed serif, in bright yellow with no stroke. Each word slides in from the left.",
    answer:
      "Yellow Serif shows one word at a time in Imbue, a tall condensed serif, in bright yellow with no stroke or shadow, sliding in from the left along the baseline. It suits fashion, editorial and luxury-style clips over dark footage.",
    body: [
      "Yellow Serif is the editorial choice. Imbue is a narrow, high-contrast serif, and here it is set in a bright yellow with nothing behind it, which gives it a magazine feel on dark or muted footage.",
      "Each word slides in 18px from the left over roughly the first third of its time on screen, anchored at the baseline, so the text seems to be typeset in front of the viewer.",
      "Use it for fashion, beauty, luxury and editorial content, and for quotes over cinematic b-roll. On bright video it will struggle without a stroke; Slab Yellow or Yellow Comic hold up better there.",
    ],
    language: "Italian",
    related: RELATED_D,
    spec: {
      font: "Imbue",
      case: "As spoken",
      highlight: "Whole word in yellow, shown one word at a time",
      animation: "Word slides in from the left",
      background: "None",
      bestFor: "Fashion, editorial and luxury clips over dark footage",
    },
  }),
  define({
    slug: "bubble-pink",
    name: "Bubble Pink",
    templateId: 104,
    grouping: "line",
    quickPanel: false,
    descriptor: "Rounded Fredoka One with a dusky red stroke, the spoken word in pink.",
    seoDescription:
      "Bubble Pink sets full lines in rounded Fredoka One with a 2.5px dusky red stroke. The spoken word turns soft pink and keywords peach, with no animation.",
    answer:
      "Bubble Pink sets full lines in Fredoka One, a rounded bubbly sans, in white with a 2.5px dusky red stroke, turning the spoken word soft pink and keywords peach. It suits beauty, lifestyle and kids content where a friendly, rounded look fits.",
    body: [
      "Bubble Pink is the friendliest style in the gallery. Fredoka One has round, soft letterforms, and the 2.5px stroke is a muted red rather than black, so the whole caption reads warm instead of harsh.",
      "The spoken word flips from white to a soft pink, and when keyword highlighting is on, keywords use a peach tone, which gives two levels of emphasis on one line. There is no entrance or exit animation.",
      "It fits beauty, lifestyle, food and family content. Pink Comic is the louder alternative with Bangers and a slide-up entrance.",
    ],
    language: "Dutch",
    related: RELATED_B,
    spec: {
      font: "Fredoka One",
      case: "Sentence case",
      highlight: "Spoken word in pink, keywords in peach",
      animation: "None",
      background: "2.5px dusky red stroke, no box",
      bestFor: "Beauty, lifestyle, food and family clips",
    },
  }),
  define({
    slug: "yellow-comic",
    name: "Yellow Comic",
    templateId: 108,
    grouping: "word",
    descriptor: "Yellow Bangers with a yellow glow and no stroke, tilting in word by word.",
    seoDescription:
      "Yellow Comic shows one Bangers word at a time in bright yellow with a matching glow and no stroke, rotating slightly into place. Loud and warm at once.",
    answer:
      "Yellow Comic shows one word at a time in Bangers comic lettering, bright yellow with a matching yellow glow and no stroke, rotating a few degrees into place. It suits comedy, gaming and reaction clips that want the comic energy without a black outline.",
    body: [
      "Yellow Comic keeps the Bangers typeface but drops the stroke. The text is a bright yellow with a 10px yellow glow, so on dark footage each word looks like it is lit from within.",
      "Each word rotates in from a slight counter-clockwise tilt to level over the first 30 percent of its time on screen, the same flick used by Brush Tilt, which suits the hand-drawn comic letterforms.",
      "Use it on comedy, gaming and reaction content over darker scenes. Comic Red and Comic Zoom keep the stroke for brighter footage.",
    ],
    language: "Spanish",
    related: RELATED_C,
    spec: {
      font: "Bangers",
      case: "Capitals, from the typeface",
      highlight: "Whole word in yellow, shown one word at a time",
      animation: "Word rotates slightly into place",
      background: "Yellow glow, no stroke or box",
      bestFor: "Comedy, gaming and reaction clips over dark footage",
    },
  }),
  define({
    slug: "yellow-wiggle",
    name: "Yellow Wiggle",
    templateId: 113,
    grouping: "word",
    descriptor: "Uppercase yellow Montserrat Bold with no stroke, wiggling into place.",
    seoDescription:
      "Yellow Wiggle shows one uppercase Montserrat Bold word at a time in bright yellow with no stroke, rotating in with a quick wiggle. Clean and modern.",
    answer:
      "Yellow Wiggle shows one uppercase Montserrat Bold word at a time in bright yellow with no stroke or shadow, rotating in with a quick wiggle. It suits modern talking-head clips, tech and productivity content over dark or blurred backgrounds.",
    body: [
      "Yellow Wiggle is a clean geometric style. Montserrat Bold in uppercase is set in a vivid yellow with nothing behind it, so it reads as modern and minimal rather than comic.",
      "Each word rotates in from a slight tilt to level over the first 30 percent of its time on screen with an ease in and out, a wiggle that gives the otherwise flat typeface a little bounce.",
      "It is well suited to tech, productivity and creator-economy clips, especially over blurred or dark backgrounds. Clean Caps uses the same family with a stroke and a ripple for brighter footage.",
    ],
    language: "Portuguese",
    related: RELATED_A,
    spec: {
      font: "Montserrat Bold",
      case: "Uppercase",
      highlight: "Whole word in yellow, shown one word at a time",
      animation: "Word wiggles into place",
      background: "None",
      bestFor: "Tech, productivity and modern talking-head clips",
    },
  }),
  define({
    slug: "pink-comic",
    name: "Pink Comic",
    templateId: 114,
    grouping: "line",
    descriptor: "Bangers comic lettering with no stroke, the spoken word in pink, sliding up.",
    seoDescription:
      "Pink Comic sets full lines in Bangers with no stroke, turning the spoken word pink as each line slides up into place. A playful style for lifestyle clips.",
    answer:
      "Pink Comic sets full lines in Bangers comic lettering, white with no stroke or shadow, turning the spoken word soft pink as each line slides up into place. It suits playful lifestyle, beauty and friend-group content over darker footage.",
    body: [
      "Pink Comic takes the Bangers typeface and removes the outline, so the white letters sit directly on the footage and the spoken word flips to a soft pink.",
      "Each line pans up 20px into place over the first 15 percent of its duration, while the pink highlight walks across the words as they are said.",
      "Use it for lifestyle, beauty and vlog content over darker or blurred backgrounds. On bright footage pick Comic Red, which adds a 5px stroke.",
    ],
    language: "French",
    related: RELATED_D,
    spec: {
      font: "Bangers",
      case: "Capitals, from the typeface",
      highlight: "Spoken word in pink",
      animation: "Line slides up into place",
      background: "None",
      bestFor: "Lifestyle, beauty and vlog clips over dark footage",
    },
  }),
  define({
    slug: "clean-caps",
    name: "Clean Caps",
    templateId: 122,
    grouping: "line",
    descriptor: "Uppercase Montserrat Black with a thin stroke, rippling in from the centre.",
    seoDescription:
      "Clean Caps sets full lines in uppercase Montserrat Black with a 2.5px black stroke, rippling in from the centre. A modern geometric caption for any niche.",
    answer:
      "Clean Caps sets full lines in uppercase Montserrat Black, white with a 2.5px black stroke, with the words rippling in from the centre of the line outward. It suits business, education and talking-head clips that want bold captions without a comic or slab feel.",
    body: [
      "Clean Caps is the geometric all-rounder. Montserrat Black in uppercase white with a thin 2.5px black stroke is bold without being loud, and it holds up on bright and dark footage alike.",
      "Lines ripple in: each word scales from 67 percent to full size over roughly the first third of the line's time on screen, staggered outward from the centre of the line. Every word stays white, so there is no spoken-word highlight.",
      "It is a safe default for business, education, podcast and creator content. Ripple Caps uses the same ripple on single words in a condensed face.",
    ],
    language: "German",
    related: RELATED_B,
    spec: {
      font: "Montserrat Black",
      case: "Uppercase",
      highlight: "None, every word stays white",
      animation: "Words ripple in from the centre",
      background: "2.5px black stroke, no box",
      bestFor: "Business, education and podcast clips",
    },
  }),
  define({
    slug: "blue-box",
    name: "Blue Box",
    templateId: 131,
    grouping: "line",
    descriptor: "Sigmar One in a blue box with a soft shadow, words waving up one by one.",
    seoDescription:
      "Blue Box sets each line of Sigmar One in white on a bright blue rounded box with a soft shadow. Words wave up one after another. Solid on any footage.",
    answer:
      "Blue Box sets each line of Sigmar One in white inside a bright blue box with 4px rounded corners and a soft black shadow, with the words waving up one after another. It suits news, commentary and educational clips where a solid colour block keeps captions readable on any footage.",
    body: [
      "Blue Box is the boldest of the boxed styles. Sigmar One, a heavy display face, is set in white on a saturated blue box with 10px of horizontal padding and 4px corners, and a 10px black blur shadow lifts the box off the video.",
      "Words rise 6px into place one after another, staggered word by word, so each line rolls in left to right inside its box. All words stay white.",
      "Use it on news-style commentary, explainers and educational clips, and whenever the footage is too busy for stroked text. Black Box is the neutral version and Light Box the quiet one.",
    ],
    language: "Italian",
    related: RELATED_C,
    spec: {
      font: "Sigmar One",
      case: "As spoken",
      highlight: "None, every word stays white",
      animation: "Words wave up one after another",
      background: "Blue rounded box with a soft shadow",
      bestFor: "News, commentary and explainer clips",
    },
  }),
  define({
    slug: "blue-outline",
    name: "Blue Outline",
    templateId: 143,
    grouping: "word",
    quickPanel: false,
    descriptor: "Uppercase Inter Bold with a thick blue stroke, zooming out word by word.",
    seoDescription:
      "Blue Outline shows one uppercase Inter Bold word at a time in white with a 3.5px deep blue stroke, zooming out as it ends. A clean tech word style.",
    answer:
      "Blue Outline shows one uppercase Inter Bold word at a time in white with a 3.5px deep blue stroke, and each word zooms out as its time ends. It suits tech, software and productivity clips where a blue accent matches the brand.",
    body: [
      "Blue Outline uses Inter Bold, the workhorse interface typeface, in uppercase white with a 3.5px stroke in a deep blue instead of black. The coloured stroke is the whole personality of the style.",
      "Words are shown one at a time and each zooms out from full size to 70 percent in its last 15 percent on screen, handing off to the next word.",
      "Pick it for software demos, SaaS and tech commentary, and any clip where blue is the brand colour. Reveal Lavender uses the same typeface with a word-reveal effect on full lines.",
    ],
    language: "Dutch",
    related: RELATED_A,
    spec: {
      font: "Inter Bold",
      case: "Uppercase",
      highlight: "Whole word in white, shown one word at a time",
      animation: "Word zooms out at the end",
      background: "3.5px deep blue stroke, no box",
      bestFor: "Tech, software and productivity clips",
    },
  }),
  define({
    slug: "black-box",
    name: "Black Box",
    templateId: 157,
    grouping: "line",
    descriptor: "Secular One in a black rounded box, fading out line by line.",
    seoDescription:
      "Black Box sets each line of Secular One in white on a black rounded box that fades out at the end. A neutral broadcast-style caption for any footage.",
    answer:
      "Black Box sets each line of Secular One in white inside a black box with 4px rounded corners, and each line fades out as it ends. It suits interviews, documentary-style clips and news content where the captions should look like broadcast subtitles.",
    body: [
      "Black Box is the neutral boxed style. Secular One, a clean rounded sans, is set in white on a solid black box with 10px of horizontal padding and 4px corners, with no stroke or shadow, so it looks like a subtitle track from a documentary.",
      "The only motion is a fade out over the last 40 percent of each line. Every word stays white, so there is no spoken-word highlight to distract from the speaker.",
      "Use it on interviews, documentaries, news and corporate clips. Light Box is the light-mode version with a grey-to-black karaoke effect, and Blue Box the louder colour version.",
    ],
    language: "Spanish",
    related: RELATED_D,
    spec: {
      font: "Secular One",
      case: "As spoken",
      highlight: "None, every word stays white",
      animation: "Line fades out",
      background: "Black rounded box",
      bestFor: "Interviews, documentaries and news clips",
    },
  }),
  define({
    slug: "reveal-lavender",
    name: "Reveal Lavender",
    templateId: 208,
    grouping: "line",
    descriptor: "Uppercase Inter Bold that reveals each word as spoken, the current one in lavender.",
    seoDescription:
      "Reveal Lavender hides each uppercase Inter Bold word until it is spoken, lighting the current word lavender and leaving spoken words white.",
    answer:
      "Reveal Lavender keeps each uppercase Inter Bold word invisible until it is spoken, lighting the current word in lavender and leaving the words already said in white, with a soft grey glow and a zoom out at the end of each line. It suits podcast clips, quotes and educational content where viewers should read at the speaker's pace.",
    body: [
      "Reveal Lavender is a true reveal effect. The base text colour is transparent, so unspoken words are not on screen yet; the word being said appears in a pale lavender and each word turns white once spoken.",
      "Inter Bold in uppercase carries a soft grey glow at 10px blur rather than a stroke, and the full line zooms out to 70 percent in its last 15 percent before the next one takes over.",
      "It suits podcast highlights, quotes and teaching clips where reading pace should match speech. Blue Outline uses the same typeface as a one-word style with a blue stroke.",
    ],
    language: "Portuguese",
    related: RELATED_B,
    spec: {
      font: "Inter Bold",
      case: "Uppercase",
      highlight: "Words appear as spoken, the current word in lavender",
      animation: "Line zooms out at the end",
      background: "Soft grey glow, no stroke or box",
      bestFor: "Podcast highlights, quotes and teaching clips",
    },
  }),
];

export const getCaptionStyle = (slug: string) =>
  CAPTION_STYLES.find((style) => style.slug === slug);

export const captionStyleGroupingLabel = (grouping: CaptionStyleGrouping) =>
  GROUPING_LABELS[grouping];

export const otherCaptionStyles = (slug: string, count: number): CaptionStyle[] => {
  const index = CAPTION_STYLES.findIndex((style) => style.slug === slug);
  if (index < 0) return CAPTION_STYLES.slice(0, count);
  return Array.from(
    { length: Math.min(count, CAPTION_STYLES.length - 1) },
    (_, offset) => CAPTION_STYLES[(index + 1 + offset) % CAPTION_STYLES.length],
  );
};
